import React, { useEffect, useState } from "react";
import Layout from "@components/Layout";
import { Input } from "@components/ui/input";
import { API_BASE_URL } from "@lib/config";
import { useUser } from "../hooks/useUser";
import { useNavigate } from "react-router-dom";

interface TradeAudit {
  id: number;
  contract_id: string;
  action: string;
  notional: number;
  rationale: string;
  executed_by: string;
  quantity?: number;
  price?: number;
  timestamp?: string;
}

const TradeComplianceHistory: React.FC = () => {
  const { userEmail, userLoading } = useUser();
  const navigate = useNavigate();

  const [records, setRecords] = useState<TradeAudit[]>([]);
  const [filtered, setFiltered] = useState<TradeAudit[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [sortField, setSortField] = useState<keyof TradeAudit | null>(null);
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [actionFilter, setActionFilter] = useState<string>("ALL");
  const [page, setPage] = useState<number>(1);
  const pageSize = 50;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userLoading) {
      setLoading(true);
      fetch(`${API_BASE_URL}/get_trade_audit`)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status} – ${res.statusText}`);
          return res.json();
        })
        .then((json) => {
          setRecords(json);
          setFiltered(json);
        })
        .catch((err) => {
          console.error("Error fetching trade audit:", err);
          setError(err.message || "Unknown error");
        })
        .finally(() => setLoading(false));
    }
  }, [userLoading]);

  useEffect(() => {
    let data = [...records];

    if (searchText) {
      const lower = searchText.toLowerCase();
      data = data.filter(
        (r) =>
          r.contract_id.toLowerCase().includes(lower) ||
          r.rationale?.toLowerCase().includes(lower) ||
          r.executed_by?.toLowerCase().includes(lower)
      );
    }

    if (actionFilter !== "ALL") {
      data = data.filter((r) => r.action === actionFilter);
    }

    if (sortField) {
      data.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        if (aVal === undefined || bVal === undefined) return 0;
        return sortAsc
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }

    setFiltered(data);
    setPage(1);
  }, [searchText, actionFilter, sortField, sortAsc, records]);

  const exportCsv = () => {
    const header = [
      "Timestamp",
      "Contract ID",
      "Action",
      "Qty",
      "Price",
      "Notional",
      "Rationale",
      "Executed By",
    ];
    const rows = filtered.map((r) => [
      r.timestamp ?? "—",
      r.contract_id,
      r.action,
      r.quantity ?? "—",
      r.price !== undefined ? r.price.toFixed(2) : "—",
      r.notional.toFixed(2),
      r.rationale,
      r.executed_by,
    ]);
    const csvContent =
      [header, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n") + "\n";

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TradeCompliance_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  if (userLoading || loading) {
    return <Layout><p className="p-4 text-subtle italic">Loading records…</p></Layout>;
  }

  return (
    <Layout>
      <div className="px-6 pb-10">
        <h2 className="text-2xl font-semibold text-helion-navy border-b border-subtle pb-2 mb-4">
          📟 Trade & Compliance History
        </h2>

        {error && <p className="text-down font-medium">Error: {error}</p>}

        {!error && (
          <>
            <div className="flex flex-wrap gap-4 justify-between mb-4">
              <Input
                placeholder="Search by Contract, Rationale, or User…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full max-w-sm"
              />
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="px-2 py-1 border rounded text-sm"
              >
                <option value="ALL">All</option>
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
              <button
                onClick={exportCsv}
                className="px-3 py-1 bg-helion-gold text-white rounded hover:opacity-90 transition"
              >
                Export CSV
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white shadow rounded-lg text-sm">
                <thead className="bg-helion-navy text-white uppercase text-xs">
                  <tr>
                    {[ "timestamp", "contract_id", "action", "quantity", "price", "notional", "rationale", "executed_by" ].map((field) => (
                      <th
                        key={field}
                        onClick={() => {
                          if (sortField === field) {
                            setSortAsc(!sortAsc);
                          } else {
                            setSortField(field as keyof TradeAudit);
                            setSortAsc(true);
                          }
                        }}
                        className="py-2 px-3 text-left cursor-pointer hover:underline"
                      >
                        {field.replace("_", " ").toUpperCase()} {sortField === field ? (sortAsc ? "↑" : "↓") : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4 text-subtle">
                        No trade or compliance records found.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((r) => (
                      <tr
                        key={r.id}
                        className="odd:bg-subtle/10 even:bg-white hover:bg-helion-gold/10 transition"
                      >
                        <td className="py-2 px-3">
                          {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                        </td>
                        <td className="py-2 px-3">{r.contract_id}</td>
                        <td className="py-2 px-3">{r.action}</td>
                        <td className="py-2 px-3">{r.quantity ?? "—"}</td>
                        <td className="py-2 px-3">
                          {r.price !== undefined ? `$${r.price.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-2 px-3">
                          {r.notional !== undefined
                            ? `$${r.notional.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : "—"}
                        </td>
                        <td className="py-2 px-3">
                          {r.rationale
                            ? r.rationale.split(",").map((reason, idx) => (
                                <div
                                  key={idx}
                                  className="bg-subtle/15 text-subtle rounded px-2 py-0.5 text-xs mb-1 mr-1 inline-block"
                                >
                                  {reason}
                                </div>
                              ))
                            : "—"}
                        </td>
                        <td className="py-2 px-3">{r.executed_by}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-center space-x-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`px-3 py-1 rounded border ${
                      page === n
                        ? "bg-helion-navy text-white"
                        : "bg-white text-helion-navy border-subtle"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default TradeComplianceHistory;
